import React, { useRef, useEffect, useState, forwardRef } from 'react';
import { sortBy, isEqual } from 'lodash';
import { Input, MenuList, MenuItem, Chip, Box } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import useAutocomplete from '@mui/material/useAutocomplete';
import RemoveAllIcon from './RemoveAllIcon';
import Asterisk from './Asterisk';
import SkeletonWrapper from './skeleton/SkeletonWrapper';
import cx from 'classnames';

/**
 * @typedef {object} OpenSelectionListProps
 * @property {boolean} [allowAutocomplete]
 * @property {boolean} [disabled]
 * @property {string} [id]
 * @property {boolean} [showInputEndAdornment]
 * @property {React.ReactNode} [label]
 * @property {Array<*>} [list]
 * @property {(selectedValue: number | string | Array<number | string> | null) => void} [onChange]
 * @property {string} [placeholder]
 * @property {*} [value]
 * @property {boolean} [multiSelect]
 * @property {boolean} [addSelectAll]
 * @property {boolean} [required]
 * @property {boolean} [loading]
 * @property {boolean} [hasError]
 * @property {boolean} [allowDeselectSingleSelect]
 */

/**
 * @type {React.ForwardRefExoticComponent<OpenSelectionListProps & React.RefAttributes<*>>}
 * @param {OpenSelectionListProps} props
 */
const OpenSelectionList = forwardRef((props, ref) => {
  const {
    id,
    onChange,
    placeholder,
    value = null,
    label = '',
    allowAutocomplete = true,
    disabled = false,
    showInputEndAdornment = true,
    list = [],
    multiSelect = false,
    addSelectAll = false,
    required = false,
    loading = false,
    hasError = false,
    allowDeselectSingleSelect = false
  } = props;

  const selectedOptions = getSelectedOptions(list, value, multiSelect);
  const [searchText, setSearchText] = useState('');

  const pageMode = multiSelect ? PAGE_MODE.MULTI_SELECTION : PAGE_MODE.SINGLE_SELECTION;
  const settings = pageSettings.get(pageMode);

  const menuListRef = useRef(null);

  useEffect(() => {
    if (!multiSelect || !disabled) {
      return;
    }

    if (!menuListRef || !menuListRef.current) {
      return;
    }

    menuListRef.current.scrollTop = 0;
  }, [multiSelect, disabled, value]);

  const isAllSelected = addSelectAll && list?.length === value?.length;

  const showRemoveAllIcon =
    !disabled && (multiSelect || allowDeselectSingleSelect) && settings.isAnyOptionSelected(value);

  const hasSelectedOption = !multiSelect && !allowAutocomplete && selectedOptions;
  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    setAnchorEl
  } = useAutocomplete({
    getOptionLabel: o =>
      o && multiSelect && o?.length > 0 ? o.map(x => x.label).join(',') : o?.label,
    isOptionEqualToValue: (option, value) => {
      if (multiSelect && Array.isArray(value)) {
        return value.some(v => v.value === option.value);
      }
      return option.value === value?.value;
    },
    onChange: (_, value, reason) => {
      if (allowDeselectSingleSelect && selectedOptions && isEqual(value, selectedOptions)) {
        return removeAllItems();
      }
      return reason !== 'clear' && selectedValueChangeHandler(value);
    },
    open: !loading,
    options: list,
    selectOnFocus: false,
    value: selectedOptions || settings.emptyValue,
    inputValue: searchText !== '' ? searchText : hasSelectedOption ? selectedOptions.label : '',
    multiple: multiSelect
  });

  const selectedValueChangeHandler = value => {
    if (multiSelect && Array.isArray(value)) {
      const primitiveValues = value.map(option => option.value);
      onChange(primitiveValues);
      return;
    }

    if (value) {
      onChange(value.value);
      return;
    }
    onChange(settings.emptyValue);
  };

  const selectAllItems = () => {
    if (isAllSelected) {
      return;
    }

    const allValues = list.map(option => option.value);
    onChange(allValues);
  };

  const removeAllItems = () => {
    onChange(settings.emptyValue);
    setSearchText('');
  };

  return (
    <div {...getRootProps()}>
      {label && label !== '' && (
        <span {...getInputLabelProps()} className='mui-selection-list__label'>
          {label}
          {required ? <Asterisk /> : ''}
        </span>
      )}

      <div
        data-testid={`${id}-wrapper`}
        className={cx('mui-selection-list__choices-wrapper', { hasError: hasError })}>
        <div className='input-wrapper' ref={setAnchorEl}>
          <Input
            inputRef={ref}
            data-testid={`${id}-input`}
            disableUnderline
            placeholder={settings.isAnyOptionSelected(value) ? '' : placeholder}
            inputProps={{
              ...getInputProps(),
              id,
              className: cx({
                'mui-selection-list__hover-pointer': !allowAutocomplete && !disabled
              })
            }}
            onChange={event => setSearchText(event.target.value)}
            disabled={disabled}
            readOnly={!allowAutocomplete}
          />
          {multiSelect && selectedOptions && (
            <Box
              className={cx('selected-items')}
              sx={{ display: 'flex', overflow: 'auto', gap: 0.5 }}>
              {selectedOptions.map((element, index) => (
                <div key={index}>
                  <Chip
                    disabled={disabled}
                    label={element.displayName ? element.displayName : element.label}
                    onDelete={() => {
                      const updatedValues = value.filter(value => value !== element.value);
                      onChange(updatedValues);
                    }}
                  />
                </div>
              ))}
            </Box>
          )}
          {showInputEndAdornment && (
            <SearchIcon className='mui-selection-list__choice-adornment icon' />
          )}
          {showRemoveAllIcon && (
            <RemoveAllIcon className='remove-all-icon icon' removeAllHandler={removeAllItems} />
          )}
        </div>
        <SkeletonWrapper show={loading} inheritParentSize={true}>
          {list?.length ? (
            <MenuList
              {...getListboxProps()}
              ref={menuListRef}
              className='mui-selection-list__choices'
              data-testid={`${id}-list`}
              id={`${id}-list`}>
              {addSelectAll && (
                <MenuItem
                  role='option'
                  disabled={disabled}
                  key='SelectAll'
                  className={cx('mui-selection-list__choices__item', {
                    'mui-selection-list__choices__item-selected': isAllSelected
                  })}
                  onClick={selectAllItems}>
                  Select All
                </MenuItem>
              )}
              {groupedOptions.map((option, index) => (
                <MenuItem
                  {...getOptionProps({ option, index })}
                  disabled={disabled || option.disabled}
                  key={index}
                  className={cx('mui-selection-list__choices__item', {
                    'mui-selection-list__choices__item-selected': settings.isOptionSelected(
                      value,
                      option.value
                    )
                  })}
                  value={option.value}>
                  {option.labelRendered || option.label}
                </MenuItem>
              ))}
            </MenuList>
          ) : (
            <MenuList />
          )}
        </SkeletonWrapper>
      </div>
    </div>
  );
});

OpenSelectionList.displayName = 'OpenSelectionList';

const PAGE_MODE = {
  MULTI_SELECTION: 1,
  SINGLE_SELECTION: 2
};

const pageSettings = new Map([
  [
    PAGE_MODE.MULTI_SELECTION,
    {
      isAnyOptionSelected: value => value?.length > 0,
      getSelectedValue: value => sortBy(value, v => v),
      isOptionSelected: (value, optionValue) => value && value.includes(optionValue),
      emptyValue: []
    }
  ],
  [
    PAGE_MODE.SINGLE_SELECTION,
    {
      isAnyOptionSelected: value => value != null,
      getSelectedValue: value => value,
      isOptionSelected: (value, optionValue) => value === optionValue,
      emptyValue: null
    }
  ]
]);

const getSelectedOptions = (list, value, multiSelect) => {
  if (!value) return multiSelect ? [] : null;

  if (multiSelect) {
    return value.map(value => findOptionByValue(list, value)).filter(Boolean);
  }
  return findOptionByValue(list, value);
};

const findOptionByValue = (list, value) => {
  return list?.find(option => option.value === value);
};

export default OpenSelectionList;
